import {
    Directive,
    ElementRef,
    OutputEmitterRef,
    Signal,
    WritableSignal,
    afterNextRender,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { SwiperContainer } from 'swiper/element';
import { Swiper } from 'swiper/types';

import { BaseComponent } from '../base/base.component';
import { SWIPER_DEFAULT_OPTIONS, SWIPER_THEMES } from '../../tokens/swiper.token';
import { mergeSwiperOptions } from './swiper.functions';
import { SwiperElementOptions, SwiperTheme, SwiperThemes } from './swiper.types';

/**
 * Bridges a `<swiper-container>` element (Swiper Element, core build) to Angular.
 *
 * The directive holds the element back from self-initializing (`init="false"`), merges the
 * configuration from four layers — built-in baseline, `SWIPER_DEFAULT_OPTIONS`, the selected
 * theme preset and the per-instance `options` input (later layers win) — assigns the merged
 * parameters, and initializes the swiper after the first render (browser only, SSR-safe).
 * Option or theme changes after initialization are re-applied reactively.
 *
 * The consuming component needs `CUSTOM_ELEMENTS_SCHEMA`; prefer `<immo-swiper>` where that is
 * undesirable.
 */
@Directive({
    selector: 'swiper-container[immoSwiper]',
    exportAs: 'immoSwiper',
    host: {
        init: 'false',
        '[attr.data-swiper-theme]': 'theme()',
        '[attr.data-swiper-auto-slides]': "autoSlides() ? '' : null",
    },
})
export class SwiperDirective extends BaseComponent {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    protected readonly elementRef: ElementRef<SwiperContainer> = inject(ElementRef);

    protected readonly defaultOptions: SwiperElementOptions = inject(SWIPER_DEFAULT_OPTIONS);

    protected readonly themes: SwiperThemes = inject(SWIPER_THEMES);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    private readonly _swiper: WritableSignal<Swiper | null> = signal(null);

    /** The underlying Swiper instance, `null` until initialization has run (first browser render). */
    readonly swiper: Signal<Swiper | null> = this._swiper.asReadonly();

    // -----------------------------------------------------------------------------------------------------
    // @ INPUT VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** The name of the theme preset to apply. Set to `''` to opt out of theming entirely. */
    readonly theme = input<string>('default');

    /** Per-instance options (incl. `modules` / `injectStyles`), merged over defaults and theme. */
    readonly options = input<SwiperElementOptions>({});

    // -----------------------------------------------------------------------------------------------------
    // @ OUTPUT VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** Emits the Swiper instance once, directly after initialization. */
    readonly swiperInit = output<Swiper>();

    readonly slideChange = output<Swiper>();

    readonly activeIndexChange = output<Swiper>();

    readonly reachBeginning = output<Swiper>();

    readonly reachEnd = output<Swiper>();

    // -----------------------------------------------------------------------------------------------------
    // @ COMPUTED VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** The fully merged element options: defaults < theme < per-instance options. */
    readonly mergedOptions: Signal<SwiperElementOptions> = computed(() => this.buildOptions());

    /** Whether the merged options use auto-sized slides — reflected as `data-swiper-auto-slides`. */
    protected readonly autoSlides: Signal<boolean> = computed(() => this.mergedOptions().slidesPerView === 'auto');

    // -----------------------------------------------------------------------------------------------------
    // @ EFFECTS
    // -----------------------------------------------------------------------------------------------------

    private readonly applyOptionsEffect = effect(() => {
        const options = this.mergedOptions();

        if (this.IsNullOrUndefined(this._swiper())) {
            return;
        }

        this.applyOptions(options);
    });

    // -----------------------------------------------------------------------------------------------------
    // @ CONSTRUCTOR
    // -----------------------------------------------------------------------------------------------------

    constructor() {
        super();

        afterNextRender(() => this.initialize());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Slides to the next slide.
     *
     * @param speed - The transition duration in ms.
     * @returns {void}
     */
    slideNext(speed?: number): void {
        this._swiper()?.slideNext(speed);
    }

    /**
     * Slides to the previous slide.
     *
     * @param speed - The transition duration in ms.
     * @returns {void}
     */
    slidePrev(speed?: number): void {
        this._swiper()?.slidePrev(speed);
    }

    /**
     * Slides to the slide at the given index.
     *
     * @param index - The target slide index.
     * @param speed - The transition duration in ms.
     * @returns {void}
     */
    slideTo(index: number, speed?: number): void {
        this._swiper()?.slideTo(index, speed);
    }

    /**
     * Recalculates the swiper's size and position, e.g. after slides were added or removed.
     *
     * @returns {void}
     */
    update(): void {
        this._swiper()?.update();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Assigns the merged parameters to the element, wires the event outputs and initializes the
     * swiper. Runs once after the first render, so it never executes on the server.
     *
     * @returns {void}
     */
    private initialize(): void {
        const element = this.elementRef.nativeElement;
        const options = this.mergedOptions();

        Object.assign(element, options);
        this.attachEventListeners(options);
        element.initialize();

        this._swiper.set(element.swiper);
        this.swiperInit.emit(element.swiper);
    }

    /**
     * Re-applies the given parameters to the already initialized element and triggers an update.
     *
     * @param options - The merged element options to apply.
     * @returns {void}
     */
    private applyOptions(options: SwiperElementOptions): void {
        const element = this.elementRef.nativeElement;

        Object.assign(element, options);
        element.swiper?.update();
    }

    /**
     * Subscribes the directive's outputs to the element's custom events, honoring the effective
     * `eventsPrefix` (Swiper's default is `swiper`).
     *
     * @param options - The merged element options, used to resolve the event prefix.
     * @returns {void}
     */
    private attachEventListeners(options: SwiperElementOptions): void {
        const prefix = options.eventsPrefix ?? 'swiper';

        this.listen(`${prefix}slidechange`, this.slideChange);
        this.listen(`${prefix}activeindexchange`, this.activeIndexChange);
        this.listen(`${prefix}reachbeginning`, this.reachBeginning);
        this.listen(`${prefix}reachend`, this.reachEnd);
    }

    /**
     * Attaches a single DOM listener that unwraps the Swiper instance from the custom event and
     * forwards it to the given output. The listener is removed when the directive is destroyed.
     *
     * @param eventName - The (prefixed) DOM event name to listen for.
     * @param target - The output to forward the Swiper instance to.
     * @returns {void}
     */
    private listen(eventName: string, target: OutputEmitterRef<Swiper>): void {
        const element = this.elementRef.nativeElement;

        const listener = (event: Event): void => {
            const [swiper] = (event as CustomEvent<[Swiper]>).detail;

            target.emit(swiper);
        };

        element.addEventListener(eventName, listener);
        this.destroyRef.onDestroy(() => element.removeEventListener(eventName, listener));
    }

    /**
     * Builds the effective element options by merging the injected defaults, the selected theme
     * (options, modules and shadow-DOM styles) and the per-instance options, in that order.
     *
     * @returns {SwiperElementOptions} The merged element options.
     */
    private buildOptions(): SwiperElementOptions {
        const theme = this.resolveTheme();

        const themeOptions: SwiperElementOptions | null = this.IsNullOrUndefined(theme)
            ? null
            : {
                  ...theme.options,
                  modules: theme.modules ?? [],
                  injectStyles: theme.injectStyles ?? [],
              };

        return mergeSwiperOptions(this.defaultOptions, themeOptions, this.options());
    }

    /**
     * Looks up the selected theme in the injected registry. An empty theme name opts out of
     * theming; an unknown name is reported and ignored.
     *
     * @returns {SwiperTheme | null} The resolved theme, or null if none applies.
     */
    private resolveTheme(): SwiperTheme | null {
        const name = this.theme();

        if (this.IsNullOrUndefinedOrEmpty(name)) {
            return null;
        }

        const theme = this.themes[name];

        if (this.IsNullOrUndefined(theme)) {
            // Misconfiguration aid: surfaces typos without breaking the swiper (it falls back to no theme).
            console.warn(`[immoSwiper] Unknown theme '${name}'. Available themes: ${Object.keys(this.themes).join(', ')}.`);

            return null;
        }

        return theme;
    }
}
